import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { baseUrl, apiGet, apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2 } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

// Define Interface for Branch
interface BranchData {
  id: number;
  branch_name: string;
  address: string;
  email: string;
  phone: string;
  status: string;
  manager_name: string;
  branch_image: string | null;
}

export default function ViewBranch() {
  const [data, setData] = useState<BranchData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const prevSearch = React.useRef(searchTerm);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  useEffect(() => {
    const isSearchChanged = prevSearch.current !== searchTerm;
    const delay = isSearchChanged ? 500 : 0;
    prevSearch.current = searchTerm;

    const timer = setTimeout(() => {
      fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
    }, delay);
    return () => clearTimeout(timer);
  }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

  const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
    setLoading(true);
    try {
      const body: any = {
        page: page,
        per_page: size,
        search: search
      };

      if (sortCol) {
        body.sort_by = sortCol;
        body.sort_direction = sortDir;
      }

      // API now returns pagination object: { data: [], total: ... }
      const response: any = await apiPost("branches/getall", body);
      if (response && response.data) {
        setData(response.data);
        setTotalRows(response.total);
      } else {
        setData(response);
        setTotalRows(response.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: any, sortDir: string) => {
    if (column.sortField) {
      setSortColumn(column.sortField);
      setSortDirection(sortDir);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setLoading(true);
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    navigate(`${process.env.PUBLIC_URL}/add_branch`);
  };

  const handleEdit = (id: number) => {
    navigate(`${process.env.PUBLIC_URL}/edit_branch`, { state: { id: id } });
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiPost("branches/delete", { id: id });
          toast.success("Branch has been deleted successfully", { autoClose: 1000 });
          setRefresh(prev => prev + 1);
        } catch (error) {
          toast.error("Failed to delete branch", { autoClose: 1000 });
        }
      }
    });
  };

  const columns = [
    {
      name: "Sl. No",
      selector: (row: BranchData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
      width: "80px",
    },
    {
      name: "Branch Name",
      selector: (row: BranchData) => row.branch_name,
      sortable: true,
      sortField: "branch_name",
      wrap: true,
    },
    {
      name: "Address",
      selector: (row: BranchData) => row.address,
      sortable: true,
      sortField: "address",
      wrap: true,
    },
    {
      name: "Manager",
      selector: (row: BranchData) => row.manager_name || 'N/A',
      sortable: true,
      sortField: "manager_name",
      wrap: true,
    },
    {
      name: "Email",
      selector: (row: BranchData) => row.email,
      sortable: true,
      sortField: "email",
      wrap: true,
    },
    {
      name: "Phone",
      selector: (row: BranchData) => row.phone,
      sortable: true,
      sortField: "phone",
      wrap: true,
    },
    {
      name: "Status",
      cell: (row: BranchData) => (
        <span className={`badge ${row.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
          {row.status}
        </span>
      ),
      sortable: true,
      sortField: "status",
    },
    {
      name: "Actions",
      cell: (row: BranchData) => (
        <div className="d-flex justify-content-between gap-2">

          <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
            <Edit size={14} />
          </Button>
          {hasPermission('branch.delete') && (
            <Button size="sm" color="danger" onClick={() => row.id && handleDelete(row.id)} title="Delete">
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  /* 
  loading spinner handled in render, but react-data-table-component has a progressPending prop
  that is cleaner.
  */

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#e0f2f1', // Light teal background
        color: '#00695c', // Dark teal text
        minHeight: '50px',
        borderBottom: '1px solid #e0e0e0',
      },
    },
    headCells: {
      style: {
        fontSize: '15px',
        fontWeight: '600',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '50px', // override the row height
        '&:not(:last-of-type)': {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: '#e0e0e0',
        },
      },
    },
  };

  return (
    <div className='page-body'>
      <Breadcrumbs
        pageTitle="View Branch"
        parent="General"
        title="View Branch"
      />
      <Container fluid>
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                <H3 className="text-primary">Branch Overview</H3>
                {hasPermission('branch.add') && (
                  <Btn color="primary" onClick={handleAdd}>
                    <Plus size={16} /> Add Branch
                  </Btn>
                )}
              </Col>
              {hasPermission('branch.view') && (<>
                <Col xs={12} className="mb-3 d-flex justify-content-end">
                  <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                </Col>
                <div className="table-responsive">
                  <DataTable
                    data={data}
                    columns={columns}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onSort={handleSort}
                    sortServer
                    progressPending={loading}
                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                    highlightOnHover
                    customStyles={customStyles}
                  />
                </div>
              </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Container>
      <ToastContainer />
    </div>
  );
}
